import { uploadData, getUrl } from 'aws-amplify/storage';
import { v4 as uuidv4 } from 'uuid';
import { Attachment, ALLOWED_FILE_TYPES, MAX_FILE_SIZE_MB } from '../types/pms';

/**
 * Review attachments: putting the file in S3, and getting it back out again.
 *
 * Both halves were missing. `handleAddAttachment` opened a file picker, synthesised an s3Key
 * string of the form `pms-reviews/<reviewId>/attachments/<fileName>`, wrote that to DynamoDB via
 * addAttachment — and then dropped the `File` on the floor. Nothing was ever uploaded, so the key
 * named an object that had never existed. The metadata was real, which is why the count and the
 * filename displayed correctly to both the employee and the manager; the file was not.
 *
 * The second half is that neither attachment list rendered anything clickable — both printed two
 * plain `<span>`s — so "clicking the filename does nothing" was literally true: nothing was bound
 * to it. AttachmentList, which does have a download button, is not the component either review
 * view uses.
 *
 * This module owns both operations so the two views cannot drift, and so the key handling can be
 * tested without a browser. Amplify Storage is already configured (config/amplifyConfig.ts) and
 * already used by receipts and nominations, so no infrastructure changes are involved.
 */

/** Why an attachment could not be opened, in terms a person can act on. */
export type AttachmentUrlFailure = 'MISSING_KEY' | 'NOT_FOUND' | 'ERROR';

export class AttachmentUrlError extends Error {
  constructor(public readonly kind: AttachmentUrlFailure, message: string) {
    super(message);
    this.name = 'AttachmentUrlError';
  }
}

/** How long a generated download link stays valid, in seconds. */
export const ATTACHMENT_URL_TTL_SECONDS = 900;

/**
 * The S3 key an upload is stored under.
 *
 * Scoped by review id, which is what keeps one review's attachments out of another's — the same
 * boundary getAttachments already enforces on the metadata side by querying on parentId.
 *
 * A uuid is inserted before the filename because the old scheme keyed purely on the name: two
 * uploads called `report.csv` on one review resolved to the same key, so the second silently
 * replaced the first while both rows stayed in the list, each pointing at the survivor. The
 * original filename is preserved on the record for display and on the object for download.
 */
export function buildReviewAttachmentKey(
  reviewId: string,
  fileName: string,
  unique: string = uuidv4()
): string {
  const safeName = String(fileName || 'attachment').replace(/[^\w.-]+/g, '_');
  return `pms-reviews/${reviewId}/attachments/${unique}-${safeName}`;
}

/**
 * The key to hand to Amplify Storage.
 *
 * Amplify v6 re-adds the `public/` prefix itself, so a stored key that already carries one
 * resolves to `public/public/…` and 404s. StorageService.getReceiptUrl strips it for exactly this
 * reason; sharing the rule here keeps PMS from rediscovering it.
 */
export function normalizeStorageKey(s3Key: string): string {
  const trimmed = String(s3Key || '').trim();
  return trimmed.startsWith('public/') ? trimmed.slice('public/'.length) : trimmed;
}

/**
 * Whether an attachment record names an object worth trying to fetch.
 *
 * A record written before uploads existed still carries a plausible-looking key, so this cannot
 * tell "will download" from "will 404" — that is what validateObjectExistence below is for. It
 * only rejects records with nothing usable at all, so those fail with a clear message instead of
 * a request for the empty key.
 */
export function isUsableAttachmentKey(s3Key?: string | null): boolean {
  return normalizeStorageKey(s3Key || '').length > 0;
}

/** Client-side validation, matching AttachmentList's so both entry points agree. */
export function validateAttachmentFile(file: File): string | null {
  const parts = file.name.split('.');
  const ext = parts.length > 1 ? parts[parts.length - 1].toLowerCase() : '';
  if (!ALLOWED_FILE_TYPES.includes(ext)) {
    return `File type ".${ext}" is not allowed. Allowed: ${ALLOWED_FILE_TYPES.join(', ').toUpperCase()}`;
  }
  if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
    return `File exceeds the maximum size of ${MAX_FILE_SIZE_MB}MB.`;
  }
  return null;
}

/**
 * A time-limited download URL for an attachment.
 *
 * `validateObjectExistence` is the point of this function rather than an optimisation: every
 * attachment recorded before uploads worked has a key pointing at nothing, and without the check
 * those resolve to a perfectly well-formed signed URL that returns an S3 error document. The
 * manager would open a tab showing XML. Failing here instead lets the caller say the file is
 * unavailable, which is the true statement.
 *
 * Generated per click and never stored: a signed URL expires, so caching one on the record would
 * work until it quietly stopped. This is also why nothing is persisted alongside s3Key — the key
 * is the durable reference and the URL is derived from it on demand.
 */
export async function getAttachmentDownloadUrl(attachment: Pick<Attachment, 's3Key' | 'fileName'>): Promise<string> {
  const key = normalizeStorageKey(attachment.s3Key);
  if (!key) {
    throw new AttachmentUrlError(
      'MISSING_KEY',
      `"${attachment.fileName || 'This attachment'}" has no stored file reference and cannot be opened.`
    );
  }
  try {
    const result = await getUrl({
      key,
      options: { expiresIn: ATTACHMENT_URL_TTL_SECONDS, validateObjectExistence: true },
    });
    return result.url.toString();
  } catch (error: any) {
    // The check is a browser HeadObject, so it needs HEAD in the bucket's CORS AllowedMethods; a
    // bucket allowing only GET/PUT/POST 403s the preflight and every download fails as a network
    // error with the object sitting right there. An unreachable probe proves nothing about the
    // object, so fall through to an unvalidated URL rather than calling a present file missing.
    if (!error?.$metadata?.httpStatusCode &&
        /network|failed to fetch|load failed|cors|timeout|aborted/i.test(`${error?.name || ''} ${error?.message || ''}`)) {
      console.warn(
        '[PMSAttachments] Could not verify that', key,
        'exists; serving an unvalidated URL. Add HEAD to the bucket CORS AllowedMethods to restore the check.',
        error
      );
      try {
        const result = await getUrl({ key, options: { expiresIn: ATTACHMENT_URL_TTL_SECONDS } });
        return result.url.toString();
      } catch (fallbackError) {
        // Signing failed too, so this is not a CORS-blocked probe over a healthy bucket. Fall
        // through and report it the same way as any other failure.
        console.error('[PMSAttachments] Unvalidated URL also failed for', key, fallbackError);
      }
    }
    console.error('[PMSAttachments] Could not resolve download URL for', key, error);
    // A record whose object is absent is the expected legacy case, not a fault to surface raw.
    const kind: AttachmentUrlFailure = /not\s*found|NoSuchKey|404/i.test(String(error?.message || error))
      ? 'NOT_FOUND'
      : 'ERROR';
    throw new AttachmentUrlError(
      kind,
      kind === 'NOT_FOUND'
        ? `"${attachment.fileName || 'This attachment'}" is no longer available in storage.`
        : `Could not open "${attachment.fileName || 'this attachment'}". Please try again.`
    );
  }
}

/**
 * Put the file in S3 and return the key to record against the review.
 *
 * Upload first, metadata second, and deliberately not wrapped in a "record it anyway" fallback:
 * a row written for a file that failed to upload is exactly the state being fixed here — one that
 * looks like a working attachment and is not. A failed upload throws and no record is created.
 */
export async function uploadReviewAttachmentFile(reviewId: string, file: File): Promise<string> {
  const s3Key = buildReviewAttachmentKey(reviewId, file.name);
  await uploadData({
    key: s3Key,
    data: file,
    options: {
      contentType: file.type || 'application/octet-stream',
      metadata: { reviewId, originalFileName: file.name },
    },
  }).result;
  return s3Key;
}
